// React dependencies
import React, { useState, useEffect } from 'react';

// External dependencies
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

// Internal dependencies
import MText from '../../common/MText';
import MButton from '../../common/MButton';
import { IsEmpty } from '../../../common/checks';
import Colours from '../../../styles/colours';
import { Status } from '../../../common/constants';
import ProgressiveImage from '../../common/ProgressiveImage';
import convert from '../../../common/convert';
import apiService from '../../../services.js/apiService';
import Images from '../../basics/Images';

const PHOTO_DIMENSION = 40;
const COMMENT_PHOTO_DIMENSION = 200;
const ALLOWED_STATUS_TO_APPROVE = [Status.Pending, Status.Rejected];
const ALLOWED_STATUS_TO_REJECT = [Status.Pending, Status.Approved];

const CommentDetailModal = (props) => {
  const [comment, setComment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedCommentId, setLoadedCommentId] = useState(null);

  const showSkeleton = !IsEmpty(props.commentId)
    && (isLoading || loadedCommentId !== props.commentId);

  const fetchComment = async (id) => {
    try {
      setIsLoading(true);
      const result = await apiService.getPoiCommentDetail(id);
      setComment(result);
    } catch (error) {
      setComment(null);
      toast.error(error.message);
    } finally {
      setLoadedCommentId(id);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!IsEmpty(props.commentId)) {
      fetchComment(props.commentId);
    }
  }, [props.commentId]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={!IsEmpty(props.commentId)}
      onClose={props.onClose}
    >
      <DialogTitle className="pb-0">
        <div className="d-flex justify-content-between align-items-center">
          <MText size={16} bold text="Comment" />
          <IconButton aria-label="close" onClick={props.onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {
          showSkeleton ? (
            <div className="container-fluid px-0">
              <div className="row mb-4">
                {[0, 1, 2].map((item) => (
                  <div className="col-4" key={item}>
                    <div style={{ width: '100%', aspectRatio: '1 / 1' }}>
                      <Skeleton
                        animation="wave"
                        variant="rect"
                        width="100%"
                        height="100%"
                        style={{ borderRadius: 4 }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="d-flex flex-row" style={{ columnGap: 8 }}>
                <Skeleton
                  animation="wave"
                  variant="circle"
                  width={PHOTO_DIMENSION}
                  height={PHOTO_DIMENSION}
                  style={{ flexShrink: 0 }}
                />
                <div className="d-flex flex-column flex-grow-1">
                  <div className="d-flex align-items-center" style={{ height: PHOTO_DIMENSION }}>
                    <Skeleton animation="wave" width="35%" height={24} />
                  </div>
                  <Skeleton animation="wave" width="100%" height={20} />
                  <Skeleton animation="wave" width="70%" height={20} />
                </div>
              </div>
            </div>
          ) : (
            !IsEmpty(comment) && (
              <div className="container-fluid px-0">
                {
                  !IsEmpty(comment.photos) && (
                    <div className="row mb-4">
                      {comment.photos.map((photo) => (
                        <div key={photo.id} className="col-4">
                          <ProgressiveImage
                            image={convert.imageResizeUrl(
                              photo.photoUrl,
                              COMMENT_PHOTO_DIMENSION,
                              COMMENT_PHOTO_DIMENSION,
                            )}
                            style={{
                              objectFit:    'cover',
                              aspectRatio:  1,
                              borderRadius: 4,
                              cursor:       'pointer',
                            }}
                            onClick={() => props.onViewPhoto(photo.id)}
                            draggable={false}
                            width="100%"
                            height="100%"
                          />
                        </div>
                      ))}
                    </div>
                  )
                }
                <div className="d-flex flex-row" style={{ columnGap: 8 }}>
                  <ProgressiveImage
                    image={comment.shopper.photo || Images.Local.AvatarDefault}
                    style={{
                      objectFit:    'cover',
                      flexShrink:   0,
                      borderRadius: 99,
                    }}
                    width={PHOTO_DIMENSION}
                    height={PHOTO_DIMENSION}
                  />
                  <div className="d-flex flex-column">
                    <div className="w-100 d-flex align-items-center" style={{ height: PHOTO_DIMENSION }}>
                      <MText text={comment.shopper.name} style={{ fontWeight: 600 }} />
                    </div>
                    <div>
                      <MText text={comment.newText} />
                    </div>
                  </div>
                </div>
              </div>
            )
          )
        }
      </DialogContent>
      {
        showSkeleton ? (
          <DialogActions style={{ padding: '24px 16px' }}>
            <Skeleton animation="wave" variant="rect" width={88} height={36} style={{ borderRadius: 4 }} />
            <Skeleton animation="wave" variant="rect" width={88} height={36} style={{ borderRadius: 4 }} />
          </DialogActions>
        ) : (
          !IsEmpty(comment) && (
            <DialogActions style={{ padding: '24px 16px' }}>
              <div className="w-100 d-flex flex-row justify-content-center">
                {
                  ALLOWED_STATUS_TO_REJECT.includes(comment.status) && (
                    <MButton
                      className="px-5"
                      textColour={Colours.White}
                      textStyle={{ fontWeight: 500 }}
                      colour={Colours.Red}
                      text="Reject"
                      onClick={() => props.onProcessComment(comment.id, Status.Rejected)}
                    />
                  )
                }
                {
                  ALLOWED_STATUS_TO_APPROVE.includes(comment.status) && (
                    <MButton
                      className="ml-5 px-5"
                      textColour={Colours.White}
                      textStyle={{ fontWeight: 500 }}
                      colour={Colours.Green1}
                      text="Approve"
                      onClick={() => props.onProcessComment(comment.id, Status.Approved)}
                    />
                  )
                }
              </div>
            </DialogActions>
          )
        )
      }
    </Dialog>
  );
};

CommentDetailModal.defaultProps = {
  commentId: null,
};

CommentDetailModal.propTypes = {
  onClose:          PropTypes.func.isRequired,
  onProcessComment: PropTypes.func.isRequired,
  onViewPhoto:      PropTypes.func.isRequired,
  commentId:        PropTypes.number,
};

export default CommentDetailModal;
